import React, { useState, useEffect, useCallback } from 'react'
import { Form } from 'react-bootstrap'
import ReactInputMask from 'react-input-mask'
import './ExpiryDateInput.css'

const InputMask = ReactInputMask as unknown as React.FC<{
  mask: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  className: string
  placeholder: string
  id: string
  disabled?: boolean
}>

interface ExpiryDateInputProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  destructive?: boolean
  disabled?: boolean
  className?: string
  showError?: boolean
  simpleError?: boolean
  onValidationChange?: (isValid: boolean, error: string) => void
}

const ExpiryDateInput: React.FC<ExpiryDateInputProps> = ({
  id,
  label,
  value,
  onChange,
  required = false,
  destructive = false,
  disabled = false,
  className = '',
  showError = true,
  simpleError = false,
  onValidationChange,
}) => {
  const [error, setError] = useState<string>('')
  const [isValid, setIsValid] = useState<boolean>(true)

  const validateExpiryDate = useCallback(
    (inputValue: string): { isValid: boolean; error: string } => {
      // Remove any non-digit characters for validation
      const digitsOnly = inputValue.replace(/\D/g, '')

      // If empty or incomplete, don't show error yet
      if (digitsOnly.length === 0) {
        return { isValid: true, error: '' }
      }

      if (digitsOnly.length < 4) {
        const errorMessage = simpleError
          ? 'Invalid date'
          : 'Please enter a complete date (MM/YY)'
        return { isValid: false, error: errorMessage }
      }

      const month = parseInt(digitsOnly.substring(0, 2), 10)
      const year = parseInt(digitsOnly.substring(2, 4), 10)

      // Validate month (01-12)
      if (month < 1 || month > 12) {
        const errorMessage = simpleError
          ? 'Invalid date'
          : 'Month must be between 01 and 12'
        return { isValid: false, error: errorMessage }
      }

      // Get current date
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear() % 100 // Get last 2 digits
      const currentMonth = currentDate.getMonth() + 1

      // Validate year (should be current year or future)
      if (
        year < currentYear ||
        (year === currentYear && month < currentMonth)
      ) {
        const errorMessage = simpleError
          ? 'Invalid date'
          : 'Expiry date cannot be in the past'
        return { isValid: false, error: errorMessage }
      }

      return { isValid: true, error: '' }
    },
    [simpleError],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value

    // Validate the input
    const validation = validateExpiryDate(inputValue)
    setIsValid(validation.isValid)
    setError(validation.error)

    // Notify parent of validation state
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.error)
    }

    // Always call onChange to update parent state
    onChange(inputValue)
  }

  // Validate on mount and when value changes externally
  useEffect(() => {
    if (value) {
      const validation = validateExpiryDate(value)
      setIsValid(validation.isValid)
      setError(validation.error)

      // Notify parent of validation state
      if (onValidationChange) {
        onValidationChange(validation.isValid, validation.error)
      }
    }
  }, [value, onValidationChange, validateExpiryDate])

  const inputClassName = `input-mask-control ${className} ${
    destructive || !isValid ? 'is-invalid' : ''
  }`.trim()

  return (
    <Form.Group controlId={id} className="expiry-date-input">
      <Form.Label>
        {label}
        {required && <span className="text-danger"> *</span>}
      </Form.Label>
      <InputMask
        mask="99/99"
        value={value}
        onChange={handleInputChange}
        className={inputClassName}
        placeholder="MM/YY"
        id={id}
        disabled={disabled}
      />
      {showError && error && (
        <div className="error-text expiry-error">{error}</div>
      )}
    </Form.Group>
  )
}

export default ExpiryDateInput
